(() => {
  'use strict';

  if (window.__smoofStandingProductViewV2) return;

  const PROFILE = window.SmoofProductProfiles?.current;
  const PRESENTATION = PROFILE?.scene?.presentation;
  const PROFILE_ADAPTER_ENABLED = PROFILE?.orientation === 'standing' ||
    PRESENTATION?.adapter === 'homography-product';
  if (!PRESENTATION?.enabled || !PROFILE_ADAPTER_ENABLED) return;

  window.__smoofStandingProductViewV2 = true;
  window.__smoofQhsProductViewV2 = true;

  const VERSION = 'standing-product-view-v2.0.0';
  const SCRIPT_URL = document.currentScript?.src || '';
  const ROOT = document.documentElement;
  const APERTURE_GEOMETRY = PRESENTATION.apertureGeometry;
  const EDITOR_BASE_GEOMETRY = PRESENTATION.editorBaseGeometry || {
    left: -23.02, top: -23.02, width: 146.04, height: 146.04
  };
  const ARTWORK_SURFACE_RATIO = Number(PRESENTATION.artworkSurfaceRatio) || 0.7068;
  const PRINT_CLIP_RADIUS_RATIO = 0.3525;
  const ARTWORK_OVERSCAN = Number(PRESENTATION.artworkOverscan) || 1;
  const CONFIGURED_FRONT_ARTWORK_SCALE = Number(PRESENTATION.frontArtworkScale) || 1;
  const THREE_QUARTER_ARTWORK_OVERSCAN = Number(PRESENTATION.threeQuarterArtworkOverscan) || 1;
  const THREE_QUARTER_VIEW_SCALE = Number(PRESENTATION.threeQuarterViewScale) || 1.3;
  const BASE_SHADOW_MODE = PRESENTATION.baseShadowMode === 'asset' ? 'asset' : 'runtime';
  const PROJECTION = PRESENTATION.projection || {};
  const CALIBRATION_MODE = new URLSearchParams(window.location.search).get('smoof_calibrate');
  const CALIBRATION_ENABLED = CALIBRATION_MODE === 'standing-3q';
  const FRONT_CALIBRATION_ENABLED = CALIBRATION_MODE === 'standing-front';
  const CALIBRATION_STORAGE_KEY = `smoof-standing-calibration:${PROJECTION.calibrationId || PROFILE.id}`;
  const FRONT_CALIBRATION_STORAGE_KEY = `smoof-standing-front-scale:${PROFILE.id}`;
  const BASE_OBJECT_BOUNDS = Object.freeze(PRESENTATION.baseObjectBounds || {
    front: Object.freeze({ left: 21.65, top: 21.4, width: 56.65, height: 57 }),
    threeQuarter: Object.freeze({ left: 25.35, top: 21.55, width: 49.35, height: 56.95 })
  });
  const ASSET_PREFIX = String(PRESENTATION.assetPrefix || PROFILE.id || 'standing');
  const PROFILE_ASSETS = PRESENTATION.assets
    ? Object.freeze(Object.fromEntries(
        Object.entries(PRESENTATION.assets).map(([key, value]) => [
          key,
          Object.freeze({
            front: assetUrl(String(value?.front || '')),
            threeQuarter: assetUrl(String(value?.threeQuarter || ''))
          })
        ])
      ))
    : null;
  const ASSETS = Object.freeze(
    Object.fromEntries(['oak', 'earth', 'obsidian'].map(wood => [
      wood,
      Object.freeze({
        front: assetUrl(`${ASSET_PREFIX}_${wood}_front.png`),
        threeQuarter: assetUrl(`${ASSET_PREFIX}_${wood}_3q.png`)
      })
    ]))
  );

  let threeQuarterActive = CALIBRATION_ENABLED;
  let activeSurface = 'front';
  let renderTimer = 0;
  let renderRevision = 0;
  let artworkObjectUrl = '';
  let lastWood = '';
  let calibrationCorners = readCalibrationCorners();
  let frontArtworkScale = readFrontArtworkScale();
  let designRenderer = window.SmoofDesignRenderer || null;
  const presentationLayerStyles = new WeakMap();
  const PRESENTATION_LAYER_SELECTORS = Object.freeze([
    'canvas',
    '[data-smoof-guide-overlay]',
    '[data-smoof-alignment-guide-overlay]',
    '[data-smoof-product-shadow]',
    '[data-smoof-background-color]',
    '[data-smoof-background-image]',
    '[data-smoof-background-hit]',
    '[data-smoof-payment-frame]',
    '[data-smoof-qr-frame]',
    '[data-smoof-nfc-frame]',
    '[data-smoof-movable-logo]',
    '[data-smoof-qr-white-panel]',
    '[data-smoof-native-arrow-eraser-overlay]',
    '[data-smoof-arrow-overlay]',
    '[data-smoof-text-overlay]',
    '[data-smoof-logo-copy-overlay]',
    '[data-smoof-logo-duplicate-overlay]',
    '[data-smoof-number-brand-overlay]',
    '[data-smoof-custom-pictogram-layer]',
    '[data-smoof-vector-preview]'
  ]);

  function assetUrl(name) {
    if (!SCRIPT_URL) return name;
    const url = new URL(name, SCRIPT_URL);
    const assetVersion = String(PRESENTATION.assetVersion || '').trim();
    if (assetVersion) url.searchParams.set('smoof_asset', assetVersion);
    return url.href;
  }

  function normalized(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isEnglish() {
    return normalized(document.documentElement.lang).startsWith('en');
  }

  function selectedWood() {
    const state = window.SmoofProductVariant?.get?.() || {};
    const values = [state.woodCode, state.materialCode];

    document.querySelectorAll(
      'variant-picker input[type="radio"]:checked, variant-selects input[type="radio"]:checked, ' +
      'variant-picker select, variant-selects select, form[action*="/cart/add"] select[name^="options"]'
    ).forEach(control => {
      values.push(control.value || '');
      values.push(control.selectedOptions?.[0]?.textContent || '');
      values.push(control.closest('label')?.textContent || '');
    });

    const text = normalized(values.join(' '));
    if (text.includes('obsidian') || text.includes('schwarz')) return 'obsidian';
    if (text.includes('earth') || text.includes('braun') || text.includes('dunkel')) return 'earth';
    return 'oak';
  }

  function editorRoot() {
    return document.querySelector('.po-livePreview-root');
  }

  function stage() {
    return document.querySelector('.po-livePreview-stage .konvajs-content');
  }

  function ensureStyles() {
    if (document.querySelector('[data-smoof-qhs-view-styles]')) return;
    const style = document.createElement('style');
    style.dataset.smoofQhsViewStyles = VERSION;
    style.textContent = `
      [data-smoof-qhs-product-view] {
        position: absolute; inset: 0; overflow: hidden; background: #fff; pointer-events: none;
      }
      [data-smoof-qhs-product-view] > img { position: absolute; display: block; pointer-events: none; user-select: none; }
      [data-smoof-qhs-base-shadow] { position: absolute; pointer-events: none; }
      [data-smoof-qhs-base-shadow] { z-index: 1; background: transparent; }
      [data-smoof-qhs-artwork] { z-index: 2; object-fit: fill; }
      [data-smoof-qhs-gloss] {
        position: absolute; z-index: 4; overflow: visible; pointer-events: none;
        transform-origin: 0 0;
      }
      [data-smoof-qhs-product-view][data-smoof-profile-id="base-rhs-wood-double"]
        [data-smoof-qhs-gloss],
      [data-smoof-qhs-product-view][data-smoof-profile-id="base-qhs-wood-double"]
        [data-smoof-qhs-gloss] {
        z-index: 2;
      }
      [data-smoof-qhs-product-view][data-smoof-profile-id="base-rml-stainless-epoxy-single"]
        [data-smoof-qhs-gloss] {
        z-index: 2;
      }
      [data-smoof-qhs-product-view][data-smoof-profile-id="base-rhl-wood-single"]
        [data-smoof-qhs-gloss] {
        z-index: 2;
      }
      [data-smoof-qhs-product-view][data-smoof-profile-id="base-qhl-wood-single"]
        [data-smoof-qhs-gloss] {
        z-index: 2;
      }
      [data-smoof-qhs-base] { z-index: 3; max-width: none !important; object-fit: contain; }
      [data-smoof-qhs-standard-dome] {
        position: absolute; overflow: hidden; border-radius: 50%; pointer-events: none;
      }
      [data-smoof-qhs-standard-dome],
      [data-smoof-qhs-standard-depth],
      [data-smoof-qhs-standard-wash],
      [data-smoof-qhs-standard-highlight],
      [data-smoof-qhs-standard-rim] { display: block !important; }
      [data-smoof-qhs-standard-depth] {
        position: absolute; inset: 0; border-radius: 50%; pointer-events: none;
        background: radial-gradient(circle at 50% 44%, transparent 0%, transparent 76%,
          rgba(20,25,34,.025) 84%, rgba(20,25,34,.12) 97%, rgba(20,25,34,.17) 100%);
        box-shadow: inset 0 3px 5px rgba(255,255,255,.28),
          inset 0 -9px 16px rgba(20,25,34,.1);
      }
      [data-smoof-qhs-standard-wash] {
        position: absolute; inset: 0; border-radius: 50%; pointer-events: none;
        background: radial-gradient(ellipse at 24% 1%, rgba(255,255,255,.58) 0%,
          rgba(255,255,255,.24) 8%, rgba(255,255,255,.07) 17%, transparent 27%);
        mix-blend-mode: screen; opacity: .76; transition: transform 180ms ease-out;
      }
      [data-smoof-qhs-standard-highlight] {
        position: absolute; left: 5%; top: 3%; width: 90%; height: 90%; box-sizing: border-box;
        border-radius: 50%; border-top: 4px solid rgba(255,255,255,.82);
        border-left: 1px solid rgba(255,255,255,.2);
        border-right: 1px solid rgba(255,255,255,.1);
        background: transparent; filter: blur(1px); mix-blend-mode: screen; opacity: .86;
        transform: rotate(-7deg); transform-origin: center;
        transition: transform 180ms ease-out, opacity 180ms ease-out; pointer-events: none;
      }
      [data-smoof-qhs-standard-highlight]::after {
        content: ""; position: absolute; inset: 3.5%; box-sizing: border-box;
        border-radius: 50%; border-top: 1.5px solid rgba(255,255,255,.74);
        background: transparent; filter: blur(.45px); opacity: .9;
      }
      [data-smoof-qhs-standard-rim] {
        position: absolute; inset: 0; box-sizing: border-box; pointer-events: none;
        border: 1px solid rgba(255,255,255,.34); border-radius: 50%;
        box-shadow: inset 0 0 0 2px rgba(20,25,34,.08),
          inset 0 0 0 5px rgba(255,255,255,.075),
          inset 2px 3px 5px rgba(255,255,255,.24),
          inset -3px -5px 8px rgba(20,25,34,.1);
      }
      html[data-smoof-qhs-mode="product"] [data-smoof-qhs-product-view] {
        z-index: 73; background: transparent;
      }
      html[data-smoof-qhs-mode] .product__media { background-color: #fff !important; }
      html[data-smoof-product-profile="base-rml-stainless-epoxy-single"][data-smoof-qhs-mode="product"]
        .product__media { background-color: transparent !important; }
      html[data-smoof-product-profile="base-rhl-wood-single"][data-smoof-qhs-mode]
        [data-smoof-qhs-product-view][data-smoof-preserve-native-front="true"][data-smoof-view="front"] {
        display: none !important;
      }
      html[data-smoof-product-profile="base-rhl-wood-single"][data-smoof-qhs-mode]
        [data-smoof-product-scene-base] { display: none !important; }
      html[data-smoof-product-profile="base-qhl-wood-single"][data-smoof-qhs-mode]
        [data-smoof-product-scene-base] { display: none !important; }
      slider-element[data-smoof-standing-editor-slider] > .product__media-list {
        visibility: hidden !important; opacity: 0 !important; pointer-events: none !important;
      }
      [data-smoof-standing-editor-viewport] { background: #fff !important; }
      [data-smoof-standing-editor-viewport] > .indicators { display: none !important; }
      html[data-smoof-qhs-mode="product"] [data-smoof-background-base] {
        clip-path: circle(35.34% at 50% 50%);
        -webkit-clip-path: circle(35.34% at 50% 50%);
      }
      html[data-smoof-qhs-mode="print"] [data-smoof-qhs-product-view] { display: none !important; }
      html[data-smoof-qhs-mode] [data-smoof-surface-overlay] { display: none !important; }
      html[data-smoof-qhs-mode="three-quarter"] [data-smoof-qhs-product-view] {
        z-index: 2147483500; pointer-events: auto; background: #fff;
      }
      html[data-smoof-qhs-mode="three-quarter"] [data-smoof-standing-live-canvas] {
        opacity: 0 !important;
      }
      [data-smoof-qhs-3d-toggle] {
        appearance: none; border: 0; border-radius: 9px; background: transparent; color: #171719;
        height: 34px; min-height: 34px; padding: 0 12px; display: block;
        font-family: inherit; font-size: 12px; font-weight: 650; line-height: normal; white-space: nowrap; cursor: pointer;
        transition: background-color .16s ease, color .16s ease, transform .16s ease;
      }
      [data-smoof-qhs-3d-toggle][data-smoof-ui-tone="dark"] { color: #f7f7f8; }
      [data-smoof-qhs-3d-toggle]:hover:not(:disabled) { background: rgba(127,127,127,.16); transform: translateY(-1px); }
      [data-smoof-qhs-3d-toggle][aria-pressed="true"] {
        background: rgb(31,31,33); color: #fff; box-shadow: 0 3px 9px rgba(0,0,0,.14);
      }
      [data-smoof-qhs-3d-toggle][data-smoof-ui-tone="dark"][aria-pressed="true"] {
        background: rgba(255,255,255,.14); color: #fff;
      }
      [data-smoof-qhs-3d-toggle]:disabled { opacity: .42; cursor: default; }
      html[data-smoof-qhs-mode] [data-smoof-product-profile-scene],
      html[data-smoof-qhs-mode] [data-smoof-product-profile-badge] { display: none !important; }
      [data-smoof-standing-calibration-grid] {
        position: absolute; z-index: 8; pointer-events: none; max-width: none !important;
        transform-origin: 0 0; opacity: .82; mix-blend-mode: multiply;
      }
      [data-smoof-standing-calibration-handle] {
        position: absolute; z-index: 9; width: 22px; height: 22px; margin: -11px 0 0 -11px;
        border: 3px solid #fff; border-radius: 50%; background: #ff2d8d;
        box-shadow: 0 2px 10px rgba(0,0,0,.32); pointer-events: auto; cursor: grab;
        touch-action: none;
      }
      [data-smoof-standing-calibration-handle]::after {
        content: attr(data-label); position: absolute; left: 15px; top: -7px;
        padding: 3px 6px; border-radius: 5px; background: #171719; color: #fff;
        font: 700 11px/1.2 system-ui, sans-serif; white-space: nowrap;
      }
      [data-smoof-standing-calibration-panel] {
        position: fixed; z-index: 2147483646; right: 18px; bottom: 18px; width: min(390px, calc(100vw - 36px));
        padding: 14px; border-radius: 14px; background: rgba(24,24,26,.96); color: #fff;
        box-shadow: 0 12px 40px rgba(0,0,0,.28); font: 13px/1.4 system-ui, sans-serif;
      }
      [data-smoof-standing-calibration-panel] strong { display: block; margin-bottom: 5px; font-size: 15px; }
      [data-smoof-standing-calibration-panel] textarea {
        width: 100%; min-height: 92px; margin: 10px 0; padding: 8px; box-sizing: border-box;
        border: 1px solid #555; border-radius: 8px; background: #0d0d0e; color: #fff;
        font: 11px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace; resize: vertical;
      }
      [data-smoof-standing-calibration-panel] button {
        min-height: 34px; margin-right: 7px; padding: 0 11px; border: 0; border-radius: 8px;
        background: #fff; color: #111; font: 700 12px/1 system-ui, sans-serif; cursor: pointer;
      }
      [data-smoof-standing-front-calibration-panel] input[type="range"] {
        width: 100%; margin: 13px 0 8px; accent-color: #ff2d8d;
      }
      [data-smoof-standing-front-calibration-panel] output {
        display: block; margin-bottom: 10px; font: 800 18px/1 system-ui, sans-serif;
      }
    `;
    document.head.appendChild(style);
  }

  function ensureThreeQuarterToggle() {
    document.querySelector('[data-smoof-qhs-view-controls]')?.remove();
    let switcher = document.querySelector('[data-smoof-surface-switcher]');
    if (!switcher && PRESENTATION.singleSurfaceToolbar === true) {
      const host = document.querySelector('[data-smoof-ui-commandbar-host]');
      if (host) {
        host.dataset.smoofSurfaceHost = 'true';
        switcher = document.createElement('section');
        switcher.dataset.smoofSurfaceSwitcher = 'true';
        switcher.dataset.smoofSingleSurfacePresentation = 'true';
        switcher.dataset.smoofEditorItem = 'modern-ui-control';
        switcher.setAttribute('aria-label', isEnglish() ? 'Product view' : 'Produktansicht');
        host.appendChild(switcher);
      }
    }
    if (!switcher) return null;

    const existing = [...switcher.querySelectorAll(
      '[data-smoof-qhs-3d-toggle], [data-smoof-qhs3d-toggle]'
    )];
    let button = existing.shift() || null;
    existing.forEach(duplicate => duplicate.remove());
    if (button) {
      button.removeAttribute('data-smoof-qhs3d-toggle');
      button.setAttribute('data-smoof-qhs-3d-toggle', VERSION);
      bindThreeQuarterToggle(button);
      syncToggleTone(button, switcher);
      return button;
    }

    button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('data-smoof-qhs-3d-toggle', VERSION);
    button.textContent = '3D-Ansicht';
    bindThreeQuarterToggle(button);
    switcher.appendChild(button);
    syncToggleTone(button, switcher);
    return button;
  }

  function bindThreeQuarterToggle(button) {
    if (!button || window.__smoofStandingProductViewToggle === VERSION) return;
    Object.defineProperty(window, '__smoofStandingProductViewToggle', {
      configurable: true,
      value: VERSION
    });
    window.addEventListener('click', async event => {
      const button = event.target?.closest?.('[data-smoof-qhs-3d-toggle]');
      if (!button) return;
      if (button.disabled) return;
      designRenderer = window.SmoofDesignRenderer || designRenderer;
      if (threeQuarterActive) {
        threeQuarterActive = false;
        render();
        return;
      }

      button.disabled = true;
      const blob = await captureArtworkBlob();
      threeQuarterActive = true;
      render();
      button.disabled = false;

      if (blob) {
        applyArtworkBlob(blob);
      } else {
        scheduleArtwork(true);
      }
    }, true);
  }

  function syncToggleTone(button, switcher) {
    const color = getComputedStyle(switcher).backgroundColor.match(/[\d.]+/g)?.map(Number) || [];
    const [red = 255, green = 255, blue = 255] = color;
    const luminance = red * .2126 + green * .7152 + blue * .0722;
    button.dataset.smoofUiTone = luminance < 128 ? 'dark' : 'light';
  }

  function isolateProductGallery() {
    const slider = editorRoot()?.closest('slider-element');
    if (!slider) return;
    slider.setAttribute('data-smoof-standing-editor-slider', VERSION);
    slider.parentElement?.setAttribute('data-smoof-standing-editor-viewport', VERSION);
  }

  function presentationLayers(targetStage) {
    return [...targetStage.querySelectorAll(PRESENTATION_LAYER_SELECTORS.join(','))]
      .filter(element => !element.closest('[data-smoof-qhs-product-view]'))
      .filter(element => !element.parentElement?.closest('[data-smoof-qhs-product-view]'))
      .filter((element, index, elements) => !elements.some((candidate, candidateIndex) =>
        candidateIndex !== index && candidate.contains(element)
      ));
  }

  function rememberPresentationLayerStyle(element) {
    if (presentationLayerStyles.has(element)) return;
    const properties = ['transform', 'transform-origin', 'clip-path', '-webkit-clip-path', 'opacity'];
    presentationLayerStyles.set(element, Object.fromEntries(properties.map(property => [
      property,
      {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property)
      }
    ])));
  }

  function restorePresentationLayerStyle(element) {
    const original = presentationLayerStyles.get(element);
    if (!original) return;
    Object.entries(original).forEach(([property, state]) => {
      if (state.value) element.style.setProperty(property, state.value, state.priority);
      else element.style.removeProperty(property);
    });
  }

  function tagPresentationLayers(targetStage) {
    presentationLayers(targetStage).forEach(element => {
      rememberPresentationLayerStyle(element);
      element.dataset.smoofStandingArtworkLayer = VERSION;
      if (element.tagName === 'CANVAS') element.dataset.smoofStandingLiveCanvas = VERSION;
    });
  }

  function syncArtworkPresentation(targetStage, mode) {
    presentationLayers(targetStage).forEach(element => {
      rememberPresentationLayerStyle(element);
      restorePresentationLayerStyle(element);

      if (mode === 'product') {
        const original = presentationLayerStyles.get(element)?.transform?.value || '';
        const baseTransform = original && original !== 'none' ? `${original} ` : '';
        const alreadyCentered = element.matches(
          '[data-smoof-background-hit], [data-smoof-product-shadow]'
        );
        if (!alreadyCentered) {
          const originX = targetStage.clientWidth / 2 - element.offsetLeft;
          const originY = targetStage.clientHeight / 2 - element.offsetTop;
          element.style.setProperty('transform-origin', `${originX}px ${originY}px`, 'important');
        }
        element.style.setProperty('transform', `${baseTransform}scale(${frontArtworkScale})`, 'important');
        element.style.setProperty('opacity', '1', 'important');
        if (element.tagName === 'CANVAS') {
          element.style.setProperty('clip-path', 'circle(35.34% at 50% 50%)', 'important');
          element.style.setProperty('-webkit-clip-path', 'circle(35.34% at 50% 50%)', 'important');
        }
      } else if (mode === 'three-quarter') {
        element.style.setProperty('opacity', '0', 'important');
      }
    });
  }

  function ensureScene(targetStage) {
    let scene = targetStage.querySelector('[data-smoof-qhs-product-view]');
    if (scene) return scene;

    scene = document.createElement('div');
    scene.dataset.smoofQhsProductView = VERSION;
    scene.dataset.smoofProfileId = PROFILE.id;
    scene.dataset.smoofPreserveNativeFront = String(PRESENTATION.preserveNativeFront === true);
    scene.setAttribute('aria-hidden', 'true');

    const baseShadow = document.createElement('div');
    baseShadow.dataset.smoofQhsBaseShadow = 'true';

    const artwork = document.createElement('img');
    artwork.dataset.smoofQhsArtwork = 'true';
    artwork.alt = '';
    artwork.decoding = 'async';

    const base = document.createElement('img');
    base.dataset.smoofQhsBase = 'true';
    base.alt = '';
    base.decoding = 'async';
    base.draggable = false;

    const gloss = document.createElement('div');
    gloss.dataset.smoofQhsGloss = 'true';

    const dome = document.createElement('div');
    dome.dataset.smoofQhsStandardDome = 'true';
    const depth = document.createElement('div');
    depth.dataset.smoofQhsStandardDepth = 'true';
    const wash = document.createElement('div');
    wash.dataset.smoofQhsStandardWash = 'true';
    const highlight = document.createElement('div');
    highlight.dataset.smoofQhsStandardHighlight = 'true';
    const rim = document.createElement('div');
    rim.dataset.smoofQhsStandardRim = 'true';
    dome.append(depth, wash, highlight, rim);
    gloss.appendChild(dome);
    applyGeometry(dome, printCircleGeometry());
    bindStandardGlossPointer(targetStage);

    scene.append(baseShadow, artwork, base, gloss);
    targetStage.prepend(scene);
    return scene;
  }

  function bindStandardGlossPointer(targetStage) {
    if (targetStage.dataset.smoofStandingGlossPointer) return;
    targetStage.dataset.smoofStandingGlossPointer = VERSION;
    let pending = false;

    const reset = () => {
      const wash = targetStage.querySelector('[data-smoof-qhs-standard-wash]');
      const highlight = targetStage.querySelector('[data-smoof-qhs-standard-highlight]');
      if (wash) wash.style.transform = 'translate(0px, 0px)';
      if (highlight) {
        highlight.style.transform = 'translate(0px, 0px) rotate(-7deg)';
        highlight.style.opacity = '.86';
      }
    };

    targetStage.addEventListener('pointermove', event => {
      if (pending || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        const rect = targetStage.getBoundingClientRect();
        const wash = targetStage.querySelector('[data-smoof-qhs-standard-wash]');
        const highlight = targetStage.querySelector('[data-smoof-qhs-standard-highlight]');
        if (!rect.width || !rect.height || !wash || !highlight) return;
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        wash.style.transform = `translate(${x * 5}px, ${y * 4}px)`;
        highlight.style.transform = `translate(${x * 4}px, ${y * 3}px) rotate(-7deg)`;
        highlight.style.opacity = String(.84 + Math.max(-.05, Math.min(.07, -y * .1)));
      });
    }, { passive: true });
    targetStage.addEventListener('pointerleave', reset, { passive: true });
    reset();
  }

  function isProductPresentation() {
    return editorRoot()?.dataset.smoofProductPresentation !== 'print' &&
      ROOT.dataset.smoofProductPresentation !== 'print';
  }

  function updateThreeQuarterToggle(button, productPresentation) {
    if (!button) return;
    const label = isEnglish()
      ? (threeQuarterActive ? 'Show frontal product view' : 'Show 3D view')
      : (threeQuarterActive ? 'Frontalansicht anzeigen' : '3D-Ansicht anzeigen');
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
    button.setAttribute('aria-pressed', String(threeQuarterActive && productPresentation));
    button.disabled = !productPresentation || FRONT_CALIBRATION_ENABLED;
  }

  function expandedGeometry(geometry, factor) {
    const width = geometry.width * factor;
    const height = geometry.height * factor;
    return {
      left: geometry.left - (width - geometry.width) / 2,
      top: geometry.top - (height - geometry.height) / 2,
      width,
      height
    };
  }

  function scaledGeometry(geometry, factor) {
    return {
      ...geometry,
      left: 50 + (geometry.left - 50) * factor,
      top: 50 + (geometry.top - 50) * factor,
      width: geometry.width * factor,
      height: geometry.height * factor
    };
  }

  function artworkGeometry(aperture) {
    return expandedGeometry({
      left: aperture.left - (aperture.width / ARTWORK_SURFACE_RATIO - aperture.width) / 2,
      top: aperture.top - (aperture.height / ARTWORK_SURFACE_RATIO - aperture.height) / 2,
      width: aperture.width / ARTWORK_SURFACE_RATIO,
      height: aperture.height / ARTWORK_SURFACE_RATIO
    }, ARTWORK_OVERSCAN);
  }

  function childGeometry(parent, child) {
    return {
      left: parent.left + parent.width * child.left / 100,
      top: parent.top + parent.height * child.top / 100,
      width: parent.width * child.width / 100,
      height: parent.height * child.height / 100
    };
  }

  function applyGeometry(element, geometry) {
    Object.assign(element.style, {
      left: `${geometry.left}%`,
      top: `${geometry.top}%`,
      width: `${geometry.width}%`,
      height: `${geometry.height}%`
    });
  }

  function validCorners(value) {
    return Array.isArray(value) && value.length === 4 && value.every(point =>
      Array.isArray(point) && point.length === 2 && point.every(Number.isFinite)
    );
  }

  function printCircleGeometry() {
    const diameter = PRINT_CLIP_RADIUS_RATIO * 200;
    return {
      left: 50 - diameter / 2,
      top: 50 - diameter / 2,
      width: diameter,
      height: diameter
    };
  }

  function readCalibrationCorners() {
    if (CALIBRATION_ENABLED) {
      try {
        const stored = JSON.parse(window.localStorage.getItem(CALIBRATION_STORAGE_KEY) || 'null');
        if (validCorners(stored)) return stored;
      } catch (_error) {}
    }
    const configured = PROJECTION.threeQuarterCorners;
    if (validCorners(configured)) return configured.map(point => [...point]);
    const seed = PROJECTION.calibrationSeedCorners;
    return validCorners(seed) ? seed.map(point => [...point]) : null;
  }

  function readFrontArtworkScale() {
    if (FRONT_CALIBRATION_ENABLED) {
      const stored = Number(window.localStorage.getItem(FRONT_CALIBRATION_STORAGE_KEY));
      if (Number.isFinite(stored) && stored >= .45 && stored <= 1.15) return stored;
    }
    return CONFIGURED_FRONT_ARTWORK_SCALE;
  }

  function activeProjectionCorners() {
    if (CALIBRATION_ENABLED) return calibrationCorners;
    return validCorners(PROJECTION.threeQuarterCorners) ? PROJECTION.threeQuarterCorners : null;
  }

  function presentationProjectionCorners(corners, stageWidth) {
    if (!validCorners(corners) || CALIBRATION_ENABLED) return corners;
    const edgeBleedPx = Math.max(0, Number(PROJECTION.edgeBleedPx) || 0);
    if (!edgeBleedPx || !stageWidth) return corners;
    const bleedPercent = edgeBleedPx / stageWidth * 100;
    return corners.map(([x, y], index) => [
      x + (index === 0 || index === 3 ? -bleedPercent : bleedPercent),
      y
    ]);
  }

  function solveLinearSystem(matrix, vector) {
    const size = vector.length;
    const rows = matrix.map((row, index) => [...row, vector[index]]);
    for (let column = 0; column < size; column += 1) {
      let pivot = column;
      for (let row = column + 1; row < size; row += 1) {
        if (Math.abs(rows[row][column]) > Math.abs(rows[pivot][column])) pivot = row;
      }
      if (Math.abs(rows[pivot][column]) < 1e-9) return null;
      [rows[column], rows[pivot]] = [rows[pivot], rows[column]];
      const divisor = rows[column][column];
      for (let item = column; item <= size; item += 1) rows[column][item] /= divisor;
      for (let row = 0; row < size; row += 1) {
        if (row === column) continue;
        const factor = rows[row][column];
        for (let item = column; item <= size; item += 1) {
          rows[row][item] -= factor * rows[column][item];
        }
      }
    }
    return rows.map(row => row[size]);
  }

  function homographyCoefficients(corners, width, height, sourceGeometry) {
    if (!validCorners(corners) || !width || !height) return null;
    const geometry = sourceGeometry || { left: 0, top: 0, width: 100, height: 100 };
    const left = width * geometry.left / 100;
    const top = height * geometry.top / 100;
    const right = width * (geometry.left + geometry.width) / 100;
    const bottom = height * (geometry.top + geometry.height) / 100;
    const source = [[left, top], [right, top], [right, bottom], [left, bottom]];
    const target = corners.map(([x, y]) => [width * x / 100, height * y / 100]);
    const matrix = [];
    const vector = [];
    source.forEach(([x, y], index) => {
      const [u, v] = target[index];
      matrix.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
      vector.push(u);
      matrix.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
      vector.push(v);
    });
    return solveLinearSystem(matrix, vector);
  }

  function homographyMatrix3d(corners, width, height, sourceGeometry) {
    const h = homographyCoefficients(corners, width, height, sourceGeometry);
    if (!h) return null;
    return [
      h[0], h[3], 0, h[6],
      h[1], h[4], 0, h[7],
      0, 0, 1, 0,
      h[2], h[5], 0, 1
    ];
  }

  function applyProjectiveTransform(element, corners, width, height, sourceGeometry) {
    const matrix = homographyMatrix3d(corners, width, height, sourceGeometry);
    if (!matrix) return false;
    Object.assign(element.style, {
      left: '0px', top: '0px', width: `${width}px`, height: `${height}px`,
      clipPath: 'none', webkitClipPath: 'none', transformOrigin: '0 0',
      transform: `matrix3d(${matrix.map(value => Number(value.toFixed(10))).join(',')})`
    });
    return true;
  }

  function calibrationGridUrl() {
    const lines = [];
    for (let value = 100; value < 1000; value += 100) {
      const major = value === 500;
      lines.push(`<path d="M${value} 10V990M10 ${value}H990" stroke="${major ? '#ff2d8d' : '#00a7ff'}" stroke-width="${major ? 9 : 4}"/>`);
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">
      <circle cx="500" cy="500" r="352.5" fill="rgba(255,255,255,.16)" stroke="#ff2d8d" stroke-width="8"/>
      <g clip-path="url(#c)">${lines.join('')}</g>
      <defs><clipPath id="c"><circle cx="500" cy="500" r="352.5"/></clipPath></defs>
      <text x="500" y="173" text-anchor="middle" fill="#171719" font-family="system-ui" font-size="28" font-weight="800">EXAKTER EDITOR-KREIS</text>
      <text x="835" y="510" text-anchor="end" fill="#171719" font-family="system-ui" font-size="26" font-weight="800">RECHTS</text>
    </svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }

  function calibrationPayload() {
    return {
      engine: 'homography-v1',
      profileId: PROFILE.id,
      calibrationId: PROJECTION.calibrationId || PROFILE.id,
      sourceGeometry: 'print-circle',
      threeQuarterCorners: calibrationCorners.map(point => point.map(value => Number(value.toFixed(4))))
    };
  }

  function updateCalibrationPanel() {
    const panel = document.querySelector('[data-smoof-standing-calibration-panel]');
    const textarea = panel?.querySelector('textarea');
    if (textarea) textarea.value = JSON.stringify(calibrationPayload(), null, 2);
  }

  function saveCalibration() {
    window.localStorage.setItem(CALIBRATION_STORAGE_KEY, JSON.stringify(calibrationCorners));
    updateCalibrationPanel();
  }

  function ensureCalibrationPanel() {
    if (!CALIBRATION_ENABLED || document.querySelector('[data-smoof-standing-calibration-panel]')) return;
    const panel = document.createElement('div');
    panel.dataset.smoofStandingCalibrationPanel = VERSION;
    panel.innerHTML = `<strong>3D-Fläche kalibrieren</strong>
      <span>Die vier pinken Punkte verschieben. Raster und Motiv verwenden exakt denselben ausgeschnittenen Editor-Kreis.</span>
      <textarea readonly aria-label="Kalibrierungswerte"></textarea>
      <button type="button" data-copy>Werte kopieren</button>
      <button type="button" data-reset>Zurücksetzen</button>`;
    panel.querySelector('[data-copy]').addEventListener('click', async () => {
      const text = JSON.stringify(calibrationPayload(), null, 2);
      try { await navigator.clipboard.writeText(text); } catch (_error) {
        const textarea = panel.querySelector('textarea');
        textarea.select();
        document.execCommand('copy');
      }
    });
    panel.querySelector('[data-reset]').addEventListener('click', () => {
      const seed = PROJECTION.calibrationSeedCorners;
      if (!validCorners(seed)) return;
      calibrationCorners = seed.map(point => [...point]);
      saveCalibration();
      render();
    });
    document.body.appendChild(panel);
    updateCalibrationPanel();
  }

  function frontCalibrationPayload() {
    return {
      profileId: PROFILE.id,
      calibrationId: `${PROFILE.id}-front-scale-v1`,
      frontArtworkScale: Number(frontArtworkScale.toFixed(4))
    };
  }

  function ensureFrontCalibrationPanel() {
    if (!FRONT_CALIBRATION_ENABLED || document.querySelector('[data-smoof-standing-front-calibration-panel]')) return;
    const panel = document.createElement('div');
    panel.dataset.smoofStandingCalibrationPanel = VERSION;
    panel.dataset.smoofStandingFrontCalibrationPanel = VERSION;
    panel.innerHTML = `<strong>Frontgröße kalibrieren</strong>
      <span>Regler verschieben, bis die Druckfläche exakt in der runden BASE sitzt.</span>
      <input type="range" min="0.45" max="1.15" step="0.005" aria-label="Frontgröße">
      <output></output>
      <textarea readonly aria-label="Kalibrierungswerte"></textarea>
      <button type="button" data-copy>Werte kopieren</button>
      <button type="button" data-reset>Zurücksetzen</button>`;
    const input = panel.querySelector('input');
    const output = panel.querySelector('output');
    const textarea = panel.querySelector('textarea');
    const update = () => {
      input.value = String(frontArtworkScale);
      output.value = `${(frontArtworkScale * 100).toFixed(1)} %`;
      textarea.value = JSON.stringify(frontCalibrationPayload(), null, 2);
    };
    input.addEventListener('input', () => {
      frontArtworkScale = Number(input.value);
      window.localStorage.setItem(FRONT_CALIBRATION_STORAGE_KEY, String(frontArtworkScale));
      render();
      update();
    });
    panel.querySelector('[data-copy]').addEventListener('click', async () => {
      const text = JSON.stringify(frontCalibrationPayload(), null, 2);
      try { await navigator.clipboard.writeText(text); } catch (_error) {
        textarea.select();
        document.execCommand('copy');
      }
    });
    panel.querySelector('[data-reset]').addEventListener('click', () => {
      frontArtworkScale = CONFIGURED_FRONT_ARTWORK_SCALE;
      window.localStorage.removeItem(FRONT_CALIBRATION_STORAGE_KEY);
      render();
      update();
    });
    document.body.appendChild(panel);
    update();
  }

  function ensureCalibrationOverlay(scene, corners, width, height, sourceGeometry) {
    if (!CALIBRATION_ENABLED || !validCorners(corners)) return;
    let grid = scene.querySelector('[data-smoof-standing-calibration-grid]');
    if (!grid) {
      grid = document.createElement('img');
      grid.dataset.smoofStandingCalibrationGrid = VERSION;
      grid.alt = '';
      grid.src = calibrationGridUrl();
      scene.appendChild(grid);
    }
    applyProjectiveTransform(grid, corners, width, height, sourceGeometry);

    const labels = ['OL', 'OR', 'UR', 'UL'];
    corners.forEach((point, index) => {
      let handle = scene.querySelector(`[data-smoof-standing-calibration-handle="${index}"]`);
      if (!handle) {
        handle = document.createElement('button');
        handle.type = 'button';
        handle.dataset.smoofStandingCalibrationHandle = String(index);
        handle.dataset.label = labels[index];
        handle.setAttribute('aria-label', `Kalibrierungspunkt ${labels[index]}`);
        handle.addEventListener('pointerdown', event => {
          event.preventDefault();
          const move = moveEvent => {
            const rect = scene.getBoundingClientRect();
            calibrationCorners[index] = [
              Math.max(0, Math.min(100, (moveEvent.clientX - rect.left) / rect.width * 100)),
              Math.max(0, Math.min(100, (moveEvent.clientY - rect.top) / rect.height * 100))
            ];
            saveCalibration();
            render();
          };
          const up = () => {
            window.removeEventListener('pointermove', move, true);
            window.removeEventListener('pointerup', up, true);
          };
          window.addEventListener('pointermove', move, true);
          window.addEventListener('pointerup', up, true);
        });
        scene.appendChild(handle);
      }
      handle.style.left = `${point[0]}%`;
      handle.style.top = `${point[1]}%`;
    });
    ensureCalibrationPanel();
  }

  function suppressLegacyScene(targetStage) {
    targetStage.querySelectorAll(
      '[data-smoof-product-profile-scene], [data-smoof-product-profile-badge]'
    ).forEach(element => {
      if (!element.closest('[data-smoof-qhs-product-view]')) {
        element.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function updateScene(scene, mode) {
    const wood = selectedWood();
    const selectedAssetSet = PROFILE_ASSETS?.[wood] || PROFILE_ASSETS?.default || ASSETS[wood];
    const view = mode === 'three-quarter' ? 'threeQuarter' : 'front';
    const rawAperture = APERTURE_GEOMETRY[view];
    const aperture = mode === 'three-quarter'
      ? scaledGeometry(rawAperture, THREE_QUARTER_VIEW_SCALE)
      : rawAperture;
    const projectedArtwork = expandedGeometry(
      artworkGeometry(aperture),
      mode === 'three-quarter' ? THREE_QUARTER_ARTWORK_OVERSCAN : 1
    );
    const editorAperture = childGeometry(EDITOR_BASE_GEOMETRY, APERTURE_GEOMETRY.front);
    const frontPlane = scaledGeometry({ left: 0, top: 0, width: 100, height: 100 }, frontArtworkScale);
    const baseGeometry = mode === 'product'
      ? EDITOR_BASE_GEOMETRY
      : scaledGeometry(
          { left: 0, top: 0, width: 100, height: 100 },
          THREE_QUARTER_VIEW_SCALE
        );
    const base = scene.querySelector('[data-smoof-qhs-base]');
    const artwork = scene.querySelector('[data-smoof-qhs-artwork]');
    const gloss = scene.querySelector('[data-smoof-qhs-gloss]');
    const baseShadow = scene.querySelector('[data-smoof-qhs-base-shadow]');
    const artworkRotationDeg = Number(
      mode === 'three-quarter'
        ? (rawAperture.artworkRotationDeg ?? rawAperture.rotationDeg)
        : 0
    ) || 0;
    const artworkTransform = artworkRotationDeg ? `rotate(${artworkRotationDeg}deg)` : 'none';
    const stageWidth = scene.clientWidth || 562;
    const stageHeight = scene.clientHeight || stageWidth;
    const projectionCorners = mode === 'three-quarter'
      ? presentationProjectionCorners(activeProjectionCorners(), stageWidth)
      : null;

    base.src = selectedAssetSet[view];
    base.dataset.smoofWood = wood;
    scene.dataset.smoofSurface = activeSurface;
    scene.dataset.smoofView = view;
    artwork.style.display = mode === 'product' ? 'none' : 'block';

    let projectionSource;
    if (projectionCorners) {
      artwork.style.objectFit = 'fill';
      projectionSource = PROJECTION.sourceGeometry === 'print-circle'
        ? printCircleGeometry()
        : (PROJECTION.sourceGeometry === 'editor-trim'
            ? expandedGeometry(editorAperture, 1 / THREE_QUARTER_ARTWORK_OVERSCAN)
            : undefined);
      applyProjectiveTransform(
        artwork,
        projectionCorners,
        stageWidth,
        stageHeight,
        projectionSource
      );
    } else if (mode === 'product') {
      applyGeometry(artwork, frontPlane);
      artwork.style.clipPath = 'none';
      artwork.style.webkitClipPath = 'none';
      artwork.style.transformOrigin = '50% 50%';
      artwork.style.transform = 'none';
    } else {
      applyGeometry(artwork, projectedArtwork);
      artwork.style.clipPath = 'ellipse(35.34% 35.34% at 50% 50%)';
      artwork.style.webkitClipPath = 'ellipse(35.34% 35.34% at 50% 50%)';
      artwork.style.transformOrigin = '50% 50%';
      artwork.style.transform = artworkTransform;
    }

    if (mode === 'three-quarter' && projectionCorners) {
      applyProjectiveTransform(gloss, projectionCorners, stageWidth, stageHeight, projectionSource);
    } else if (mode === 'product') {
      applyGeometry(gloss, frontPlane);
      gloss.style.transformOrigin = '50% 50%';
      gloss.style.transform = 'none';
    } else {
      applyGeometry(gloss, { left: 0, top: 0, width: 100, height: 100 });
      gloss.style.transform = 'none';
    }
    gloss.style.filter = 'none';

    applyGeometry(base, baseGeometry);
    base.style.filter = 'none';

    if (BASE_SHADOW_MODE === 'asset') {
      baseShadow.style.display = 'none';
      baseShadow.style.boxShadow = 'none';
    } else {
      baseShadow.style.display = 'block';
      applyGeometry(baseShadow, childGeometry(baseGeometry, BASE_OBJECT_BOUNDS[view]));
      baseShadow.style.borderRadius = mode === 'three-quarter' ? '16% 13% 15% 17%' : '12%';
      baseShadow.style.backgroundColor = 'rgba(255,255,255,.01)';
      baseShadow.style.boxShadow =
        `0 ${stageWidth * .022}px ${stageWidth * .055}px ${stageWidth * -.012}px rgba(16,24,40,.20), ` +
        `0 ${stageWidth * .006}px ${stageWidth * .018}px ${stageWidth * -.004}px rgba(16,24,40,.13)`;
    }

    if (mode === 'three-quarter' && projectionCorners) {
      ensureCalibrationOverlay(scene, projectionCorners, stageWidth, stageHeight, projectionSource);
    }

    const assetKey = wood;
    if (assetKey !== lastWood) {
      lastWood = assetKey;
      Object.values(selectedAssetSet).forEach(url => {
        const preload = new Image();
        preload.src = url;
      });
    }
  }

  function render() {
    const root = editorRoot();
    const targetStage = stage();
    if (!root || !targetStage) return;

    ensureStyles();
    isolateProductGallery();
    tagPresentationLayers(targetStage);
    const productPresentation = isProductPresentation();
    if (!productPresentation) threeQuarterActive = false;
    const mode = !productPresentation ? 'print' : (threeQuarterActive ? 'three-quarter' : 'product');
    const threeQuarterToggle = ensureThreeQuarterToggle();
    const scene = ensureScene(targetStage);
    suppressLegacyScene(targetStage);
    ROOT.dataset.smoofQhsMode = mode;
    ensureFrontCalibrationPanel();
    updateThreeQuarterToggle(threeQuarterToggle, productPresentation);
    updateScene(scene, mode);
    syncArtworkPresentation(targetStage, mode);
  }

  function scheduleArtwork(immediate = false) {
    if (!isProductPresentation() || !threeQuarterActive) {
      clearTimeout(renderTimer);
      renderTimer = 0;
      return;
    }
    if (renderTimer) return;
    renderTimer = window.setTimeout(() => {
      renderTimer = 0;
      renderArtwork();
    }, immediate ? 0 : 120);
  }

  async function renderArtwork() {
    const revision = ++renderRevision;
    const blob = await captureArtworkBlob();

    try {
      if (!blob) {
        window.setTimeout(() => scheduleArtwork(), 120);
        return;
      }
      if (revision !== renderRevision || !isProductPresentation()) return;
      applyArtworkBlob(blob);
    } catch (error) {
      console.error('SMOOF QHS product preview failed', error);
    }
  }

  async function captureArtworkBlob() {
    const renderer = window.SmoofDesignRenderer || designRenderer;
    if (renderer) designRenderer = renderer;
    let blob = null;

    if (renderer?.renderPng) {
      try {
        const bytes = await renderer.renderPng();
        if (bytes instanceof Blob) {
          blob = bytes.size ? bytes : null;
        } else if (bytes instanceof ArrayBuffer || ArrayBuffer.isView(bytes)) {
          blob = bytes.byteLength ? new Blob([bytes], { type: 'image/png' }) : null;
        }
      } catch (error) {
        console.warn('SMOOF standing vector capture unavailable; using editor canvas', error);
      }
    }

    if (!blob) {
      try {
        blob = await renderStageCanvasBlob();
      } catch (error) {
        console.warn('SMOOF standing canvas capture failed', error);
      }
    }

    if (!blob) return null;
    try {
      return await clipProofToPrintCircle(blob);
    } catch (error) {
      console.warn('SMOOF standing artwork clipping failed', error);
      return null;
    }
  }

  function applyArtworkBlob(blob) {
    if (!blob) return false;
    const artwork = stage()?.querySelector('[data-smoof-qhs-artwork]');
    if (!artwork) return false;

    const url = URL.createObjectURL(blob);
    const previous = artworkObjectUrl;
    artworkObjectUrl = url;
    artwork.src = url;
    ROOT.dataset.smoofStandingArtworkReady = 'true';
    if (previous) URL.revokeObjectURL(previous);
    return true;
  }

  async function clipProofToPrintCircle(blob) {
    const sourceUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.decoding = 'sync';

    try {
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('Rendered design could not be clipped'));
        image.src = sourceUrl;
      });

      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      const output = document.createElement('canvas');
      output.width = width;
      output.height = height;
      const context = output.getContext('2d', { alpha: true });
      if (!context) return null;

      context.clearRect(0, 0, width, height);
      context.save();
      context.beginPath();
      context.arc(
        width / 2,
        height / 2,
        Math.min(width, height) * PRINT_CLIP_RADIUS_RATIO,
        0,
        Math.PI * 2
      );
      context.clip();
      context.drawImage(image, 0, 0, width, height);
      context.restore();

      return await new Promise(resolve => output.toBlob(resolve, 'image/png'));
    } finally {
      URL.revokeObjectURL(sourceUrl);
    }
  }

  async function renderStageCanvasBlob() {
    const targetStage = stage();
    if (!targetStage) return null;
    const canvases = [...targetStage.querySelectorAll(':scope > canvas')]
      .filter(canvas => canvas.width > 0 && canvas.height > 0);
    if (!canvases.length) return null;

    const width = Math.max(...canvases.map(canvas => canvas.width));
    const height = Math.max(...canvases.map(canvas => canvas.height));
    const output = document.createElement('canvas');
    output.width = width;
    output.height = height;
    const context = output.getContext('2d');
    if (!context) return null;

    canvases.forEach(canvas => {
      context.drawImage(canvas, 0, 0, width, height);
    });

    return new Promise(resolve => output.toBlob(resolve, 'image/png'));
  }

  function scheduleRender() {
    requestAnimationFrame(() => {
      render();
      scheduleArtwork();
    });
  }

  window.addEventListener('smoof:surface-change', event => {
    activeSurface = event.detail?.surface === 'back' ? 'back' : 'front';
    scheduleRender();
  });
  window.addEventListener('smoof:shopify-variant-change', scheduleRender);
  window.addEventListener('smoof:editor-reset', scheduleRender);
  document.addEventListener('input', scheduleRender, true);
  document.addEventListener('change', scheduleRender, true);
  new MutationObserver(scheduleRender).observe(document.body, { childList: true, subtree: true });
  new MutationObserver(scheduleRender).observe(ROOT, {
    attributes: true,
    attributeFilter: ['data-smoof-product-presentation']
  });
  ROOT.dataset.smoofStandingToggleRebind = VERSION;
  const toggleRebindTimer = window.setInterval(() => {
    const button = document.querySelector('[data-smoof-qhs-3d-toggle]');
    if (!button) return;
    bindThreeQuarterToggle(button);
  }, 250);
  window.addEventListener('pagehide', () => window.clearInterval(toggleRebindTimer), { once: true });

  Object.values(ASSETS).flatMap(Object.values).forEach(url => {
    const preload = new Image();
    preload.decoding = 'async';
    preload.src = url;
  });

  ROOT.dataset.smoofQhsProductViewVersion = VERSION;
  ROOT.dataset.smoofStandingProductViewVersion = VERSION;
  if (CALIBRATION_ENABLED || FRONT_CALIBRATION_ENABLED) ROOT.dataset.smoofStandingCalibration = CALIBRATION_MODE;
  render();
  scheduleArtwork(true);
})();
